import axios from "axios";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Swal from "sweetalert2";

const RegistrarTestKolb = () => {
  const navigate = useNavigate();
  const { id, testid } = useParams();

  const [aprendizId, setAprendizId] = useState(null);
  const [preguntas, setPreguntas] = useState([]);
  const [respuestas, setRespuestas] = useState([]);
  const [paginaActual, setPaginaActual] = useState(0);
  const [loading, setLoading] = useState(false);

  const preguntasPorPagina = 2;
  const totalPaginas = Math.ceil(preguntas.length / preguntasPorPagina);
  const allOptions = [1, 2, 3, 4];
  
  const [edit, setEdit] = useState(false);

  useEffect(() => {
    setEdit(!!id);
  }, [id]);

  useEffect(() => {
    setAprendizId(id);
  }, [id]);

  useEffect(() => {
    const fetchPreguntas = async () => {
      try {
        const res = await axios.get("/api/testkolb/preguntas/listall");
        const preguntasOrdenadas = res.data.preguntas;
        setPreguntas(preguntasOrdenadas);
        setRespuestas(Array(preguntasOrdenadas.length).fill({
          experienciaConcreta: '',
          observacionReflexiva: '',
          conceptualizacionAbstracta: '',
          experimentacionActiva: ''
        }));
      } catch (error) {
        console.error("Error al cargar preguntas:", error);
      }
    };
    fetchPreguntas();
  }, []);

useEffect(() => {
  const fetchTestKolb = async () => {
    if (!testid) return;

    setLoading(true);
    try {
      const response = await axios.get(`/api/testkolb/listid/${testid}`);
      const data = response.data.test;

      // Usar preguntas y respuestas directamente si el backend las trae
      if (data.preguntas) setPreguntas(data.preguntas);
      setRespuestas(data.respuestas || []);
    } catch (error) {
      console.error("Error al cargar el test de Kolb:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "No se pudo cargar el test de Kolb.",
      });
    } finally {
      setLoading(false);
    }
  };

  fetchTestKolb();
}, [testid]);


if (loading) return <div>Cargando test...</div>;


  const handlePreguntaChange = (index, nuevosValores) => {
    const actualizadas = [...respuestas];
    actualizadas[index] = nuevosValores;
    setRespuestas(actualizadas);
  };

  const validarPagina = () => {
    const inicio = paginaActual * preguntasPorPagina;
    const fin = inicio + preguntasPorPagina;
    return respuestas.slice(inicio, fin).every(r => Object.values(r).every(v => v !== ''));
  };

  const sumatoria = (clave) =>
    respuestas.reduce((acc, r) => acc + (parseInt(r[clave], 10) || 0), 0);

  const sumaEC = sumatoria('experienciaConcreta');
  const sumaOR = sumatoria('observacionReflexiva');
  const sumaCA = sumatoria('conceptualizacionAbstracta');
  const sumaEA = sumatoria('experimentacionActiva');

  const diferenciaCAEC = sumaCA - sumaEC;
  const diferenciaEAOR = sumaEA - sumaOR;

  const determinarEstilo = () => {
    if (diferenciaCAEC >= 0 && diferenciaEAOR >= 0) return 'Convergente';
    if (diferenciaCAEC < 0 && diferenciaEAOR >= 0) return 'Asimilador';
    if (diferenciaCAEC < 0 && diferenciaEAOR < 0) return 'Divergente';
    if (diferenciaCAEC >= 0 && diferenciaEAOR < 0) return 'Acomodador';
    return '';
  };

  const cleanData = () => {
    setPreguntas([]);
    setRespuestas([]);
  };

  const saveTestKolb = async () => {
    setLoading(true);
    const resultado = {
      aprendizObjectId: aprendizId,
      sumas: {
        experienciaConcreta: sumaEC,
        observacionReflexiva: sumaOR,
        conceptualizacionAbstracta: sumaCA,
        experimentacionActiva: sumaEA,
      },
      diferencias: {
        conceptualizacionVsExperiencia: diferenciaCAEC,
        experimentacionVsObservacion: diferenciaEAOR,
      },
      estiloAprendizaje: determinarEstilo(),
      respuestas: respuestas.map((r, i) => ({
        pregunta: preguntas[i]._id,
        experienciaConcreta: Number(r.experienciaConcreta),
        observacionReflexiva: Number(r.observacionReflexiva),
        conceptualizacionAbstracta: Number(r.conceptualizacionAbstracta),
        experimentacionActiva: Number(r.experimentacionActiva),
      })),
    };

    try {
      await axios.post('/api/testkolb/add', resultado);
      cleanData();

      await Swal.fire({
        icon: 'success',
        title: '¡Éxito!',
        text: 'El test fue guardado correctamente.',
        timer: 1500,
        showConfirmButton: false,
      }).then(() => navigate(`/aprendiz/listaraprendices/`));
    } catch (error) {
      if (error.response?.data?.message) {
        return Swal.fire({
          icon: 'error',
          title: 'Error',
          text: error.response.data.message,
        });
      }
    }
  };

  const updateTestKolb = async () => {
    setLoading(true);
    const resultado = {
      aprendizObjectId: aprendizId,
      sumas: {
        experienciaConcreta: sumaEC,
        observacionReflexiva: sumaOR,
        conceptualizacionAbstracta: sumaCA,
        experimentacionActiva: sumaEA,
      },
      diferencias: {
        conceptualizacionVsExperiencia: diferenciaCAEC,
        experimentacionVsObservacion: diferenciaEAOR,
      },
      estiloAprendizaje: determinarEstilo(),
      respuestas: respuestas.map((r, i) => ({
        pregunta: preguntas[i]._id,
        experienciaConcreta: Number(r.experienciaConcreta),
        observacionReflexiva: Number(r.observacionReflexiva),
        conceptualizacionAbstracta: Number(r.conceptualizacionAbstracta),
        experimentacionActiva: Number(r.experimentacionActiva),
      })),
    };

    try {
      await axios.put(`/api/testkolb/update/${testid}`, resultado);
      cleanData();

      await Swal.fire({
        icon: 'success',
        title: '¡Actualizado!',
        text: 'El test fue actualizado correctamente.',
        timer: 1500,
        showConfirmButton: false,
      }).then(() => navigate(`/aprendiz/listaraprendices/`));
    } catch (error) {
      console.error("Error al actualizar el test:", error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.response?.data?.message || 'No se pudo actualizar el test.',
      });
    }
  };


  const actions = async (e) => {
    e.preventDefault();
    edit ? saveTestKolb() : updateTestKolb();
  };

  const renderPregunta = (index, pregunta, bloqueado = false) => {
    const valores = respuestas[index] || {
      experienciaConcreta: '',
      observacionReflexiva: '',
      conceptualizacionAbstracta: '',
      experimentacionActiva: ''
    };

    const getAvailableOptions = (currentKey) => {
      const usados = Object.entries(valores)
        .filter(([k, v]) => v !== '' && k !== currentKey)
        .map(([_, v]) => Number(v));
      return allOptions.filter(o => !usados.includes(o));
    };

    const handleChange = (key, value) => {
      const nuevoValor = { ...valores, [key]: value };
      handlePreguntaChange(index, nuevoValor);
    };



    return (
      <div className="card mb-3" key={pregunta._id}>
        <div className="card-header">
          <strong>Pregunta {index + 1}</strong>
        </div>
        <div className="card-body row align-items-stretch">
          {[
            'experienciaConcreta',
            'observacionReflexiva',
            'conceptualizacionAbstracta',
            'experimentacionActiva'
          ].map((key) => (
            <div className="col-md-3 mb-3 d-flex flex-column justify-content-between" key={key}>
              <div className="flex-grow-1">
                <label className="form-label">
                  {key === 'experienciaConcreta' && 'Experiencia Concreta'}
                  {key === 'observacionReflexiva' && 'Observación Reflexiva'}
                  {key === 'conceptualizacionAbstracta' && 'Conceptualización Abstracta'}
                  {key === 'experimentacionActiva' && 'Experimentación Activa'}
                  <br />
                  <small className="text-muted d-block">{pregunta[key]?.principal}</small>
                  <small className="text-muted d-block">{pregunta[key]?.secundario}</small>
                </label>
              </div>
              <div>
                <select
                  className="form-select"
                  value={valores[key]}
                  onChange={e => handleChange(key, e.target.value)}
                  disabled={bloqueado}
                >
                  <option value="">Seleccionar</option>
                  {getAvailableOptions(key).map(op => (
                    <option key={op} value={op}>{op}</option>
                  ))}
                </select>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const handleCancel = () => {
    cleanData(); // Limpia los datos

    navigate("/aprendiz/listaraprendices"); // Siempre redirige
  };

  return (
    <div className="container py-4 px-5 shadow-lg rounded-4 bg-light mt-4 mb-5">
      <h3 className="mb-4 text-center">Test de Kolb</h3>

      {preguntas.length > 0 ? (
        <form onSubmit={actions}>
          {paginaActual < totalPaginas ? (
            <>
              {preguntas
                .slice(paginaActual * preguntasPorPagina, (paginaActual + 1) * preguntasPorPagina)
                .map((pregunta, index) =>
                  renderPregunta(paginaActual * preguntasPorPagina + index, pregunta)
                )}

              <div className="text-center">
                {paginaActual > 0 && (
                  <button
                    type="button"
                    className="btn btn-secondary me-2"
                    onClick={() => setPaginaActual(paginaActual - 1)}
                  >
                    Anterior
                  </button>
                )}
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => validarPagina() && setPaginaActual(paginaActual + 1)}
                  disabled={!validarPagina()}
                >
                  Siguiente
                </button>
              </div>
            </>
          ) : (
            <div className="card">
              <div className="card-header">Resultados Finales del Test</div>
              <div className="card-body row">
                <div className="col-md-3 mb-2">
                  <label className="form-label">Suma EC</label>
                  <input type="number" className="form-control" value={sumaEC} disabled />
                </div>
                <div className="col-md-3 mb-2">
                  <label className="form-label">Suma OR</label>
                  <input type="number" className="form-control" value={sumaOR} disabled />
                </div>
                <div className="col-md-3 mb-2">
                  <label className="form-label">Suma CA</label>
                  <input type="number" className="form-control" value={sumaCA} disabled />
                </div>
                <div className="col-md-3 mb-2">
                  <label className="form-label">Suma EA</label>
                  <input type="number" className="form-control" value={sumaEA} disabled />
                </div>

                <div className="col-md-6 mt-3">
                  <label className="form-label">Diferencia CA - EC</label>
                  <input type="number" className="form-control" value={diferenciaCAEC} disabled />
                </div>
                <div className="col-md-6 mt-3">
                  <label className="form-label">Diferencia EA - OR</label>
                  <input type="number" className="form-control" value={diferenciaEAOR} disabled />
                </div>


                <div className="col-12 text-center mt-3">
                  <h2>Estilo de Aprendizaje: {determinarEstilo()}</h2>
                </div>


                <div className="container">
                  <div className="row mt-4 mb-3">
                    {/* Botón principal: Enviar o Actualizar */}
                    <div className="col-md-6 mb-2 mb-md-0">
                      <button
                        type="submit"
                        className="btn btn-success w-100"
                        disabled={loading}
                      >
                        {loading
                          ? "Guardando..."
                          : edit
                            ? "Enviar Test"
                            : "Actualizar Test"}
                      </button>
                    </div>

                    {/* Botón secundario: Cancelar o Limpiar */}
                    <div className="col-md-6">
                      <button
                        type="button"
                        className="btn btn-danger w-100"
                        onClick={handleCancel}
                      >
                        Cancelar
                      </button>
                    </div>
                  </div>
                </div>

              </div>
            </div>
          )}
        </form>
      ) : (
        <p>Cargando preguntas...</p>
      )}
    </div>
  );
};

export default RegistrarTestKolb;
